import * as React from 'react';
import { cn } from '@noor/lib/cn';

type IconComponent = React.ComponentType<{ className?: string }>;

export type WidgetHeaderProps = Omit<React.ComponentProps<'div'>, 'title'> & {
  /** Glyph shown inside the leading tile. */
  icon: IconComponent;
  /** The widget heading. */
  title: React.ReactNode;
  /**
   * Tile treatment. `brand` (default) is the filled orange tile from the Figma
   * dashboard cards; `plain` renders the icon inline with no tile (for the
   * lighter card headers like "Reviews").
   */
  tile?: 'brand' | 'plain';
  /** Optional content pinned to the trailing edge of the header row. */
  action?: React.ReactNode;
};

/**
 * The dashboard-card header: a leading icon tile followed by a title, with an
 * optional trailing action. Encapsulates the repeated "orange square + heading"
 * pattern across the merchant dashboard widgets (Balance, Top product, Busy
 * days) so each card composes it instead of re-declaring the geometry.
 */
function WidgetHeader({
  className,
  icon: Icon,
  title,
  tile = 'brand',
  action,
  ...props
}: WidgetHeaderProps) {
  return (
    <div
      data-slot="widget-header"
      // The title inherits the root's text color, so a caller can recolor the
      // heading for a dark surface (`text-surface-inverse-foreground`) without a
      // fragile child selector; the brand tile stays orange regardless.
      className={cn('flex items-center gap-4 text-foreground', className)}
      {...props}
    >
      {tile === 'brand' ? (
        <span
          data-slot="widget-header-tile"
          className="flex size-12 shrink-0 items-center justify-center rounded-3xl bg-primary text-primary-foreground"
        >
          <Icon className="size-6" />
        </span>
      ) : (
        <Icon className="size-6 shrink-0" />
      )}
      <span className="text-xl font-semibold tracking-tight">{title}</span>
      {action !== undefined ? <div className="ml-auto">{action}</div> : null}
    </div>
  );
}

export { WidgetHeader };
